
import { Component, Vue, Emit, Watch, Prop } from "vue-property-decorator";
import rest from "@/rest";
import { namespace } from "vuex-class";
import { vuex } from "@/store";
import { signalrEventBus } from "@/main";
// components
import ExpandableCard from "../Shared/ExpandableCard.vue";
// interfaces
import { courseElementTypeItems } from "@/interfaces/Types";
import { CourseElementVm } from "@/interfaces/CourseElementVm";
import { CourseElementType } from "@/interfaces/CourseElementType";
import { FileDetailsVm } from "@/interfaces/FileDetailsVm";
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import MarkdownEditor from "../Shared/MarkdownEditor.vue";
import { ExBundleVM } from "@/interfaces/ExBundleVM";

const auth = namespace("auth");
const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    ExpandableCard,
    MarkdownEditor
  },
})
export default class VideoElementSettings extends Vue {
  // @Prop() element!: CourseElementVm;

  @auth.Getter isAdmin: any;
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle!: ExBundleVM;
  @globals.Getter selectedElement!: CourseElementVm;
  @globals.Action refreshSelectedElement!: () => Promise<void>;
  courseElementTypeItems = courseElementTypeItems;

  isBusy = false;
  isBusyOnVideoConversion = false;
  id = "";
  showMarkdownEditor = false;
  selectedLanguageCode = "de-DE";

  mounted() {
    // if (!this.selectedElement || !this.selectedBundle) return;
    if (!this.selectedBundle.sequentialModuleProcessing) {
      this.selectedElement.preventSeeking = false;
    }
  }

  created() {
    // SignalR event listener
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  // SignalR events
  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    console.log("VideoElementSettings > Background task event:", response);
    if (this.id !== response.id) return;

    if (response.status == "INF") {
      vuex.ux.SB_SUCCESS({
        message: response.message,
        timeout: 1500
      });
      return;
    }

    // Reload the element to get the updated AI summary
    await this.refreshSelectedElement();
    // Update MediaPool
    this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list

    this.isBusy = false;
  }

  @Watch('selectedElement.contentUrl')
  onContentUrlChanged() {
    if (!this.selectedElement) return;
    if (this.isYoutube) {
      this.selectedElement.enableZoom = false;
      this.selectedElement.enableDownload = false;
    }
  }

  get validVideoFile(): boolean {
    if (!this.selectedElement || !this.selectedElement.contentUrl) return false;
    const fileName = this.$globalHelper.extractFileName(this.selectedElement.contentUrl);
    const extension = this.$globalHelper.extractFileExtension(fileName).toLowerCase();
    const convertibleExtensions = ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm"];
    return convertibleExtensions.includes(extension);
  }

  get fileName(): string {
    if (!this.selectedElement || !this.selectedElement.contentUrl) return "";
    return this.$globalHelper.extractFileName(this.selectedElement.contentUrl);
  }

  get isYoutube() {
    if (this.selectedElement && this.selectedElement.contentUrl && this.selectedElement.contentUrl.toLowerCase().includes("www.youtube.com/watch"))
      return true;
    return false;
  }

  private async convertVideo(): Promise<void> {
    if (!this.selectedElement || !this.selectedBundle) return;

    this.isBusyOnVideoConversion = true;

    let params: ParamDictionary = { dictionary: {} };
    params.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    console.log("selectedElement.contentUrl", this.selectedElement.contentUrl);
    params.dictionary!["FileUrl"] = this.selectedElement.contentUrl ?? "";

    rest.url("contentManager/convertVideo").post(params)
      .then(async (response) => {
        if (this.selectedElement && response) {
          this.selectedElement.contentUrl = null;
          await this.$globalHelper.delay(5);
          this.selectedElement.contentUrl = response;
          this.selectedElement.invisible = false;
        }

        this.$root.$emit('fileListUpdate:MediaPool');
      })
      .finally(() => {
        this.isBusyOnVideoConversion = false;
      });
  }

  async onCreateAiSummary(): Promise<void> {
    if (!this.selectedElement || !this.selectedBundle) return;

    await this.$l3rnOnlineHub.ensureConnection();
    this.isBusy = true;

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["SignalRId"] = this.$l3rnOnlineHub.ConnectionId!;
    params.dictionary!["BundleId"] = this.selectedBundle.id.toString();
    params.dictionary!["FileName"] = this.$globalHelper.extractFileName(this.selectedElement.contentUrl ?? "");
    params.dictionary!["LanguageCode"] = this.selectedLanguageCode || "de-DE";

    await rest.url("contentManager/createAiSummary").post(params)
      .then(async (response) => {
        console.log("VideoElementSettings > Create AI Summary response:", response);
        if (!response || !this.selectedElement || !this.selectedBundle) return;
        this.id = response;
      })
      .catch(err => {
      })
  }

  getElementTitle(type: string | null): string {
    const element = this.courseElementTypeItems.find(el => CourseElementType[el.value] === type);
    return element ? element.label : '';
  }

  getElementIcon(type: string | null): string {
    const element = this.courseElementTypeItems.find(el => CourseElementType[el.value] === type);
    return element ? element.icon : '';
  }
}
